import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import {
  Controller,
  useForm,
  SubmitHandler,
  useFieldArray,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { InputTextField } from "../FormFields/RenderInputTextField";
import { Terminal } from "../../types/Terminal";
import { NumberField } from "../FormFields/RenderNumberField";
import { hideModal } from "../../data/modal";

import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import {
  Add as AddIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteIcon,
  Save as SaveIcon,
} from "@mui/icons-material";

import {
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  useMap,
  useMapEvents,
} from "react-leaflet";

import { InputColorField } from "../FormFields/RenderInputColorField";
import AliasFields from "./Aliases";
import { InputTextareaField } from "../FormFields/RenderInputTextareaField";

const DEFAULT_LATITUDE = 51.23102;
const DEFAULT_LONGITUDE = 4.3979;
const DEFAULT_COLOR = "#DDDDDD";
const MAP_RENDER_DELAY_MS = 700;
const REQUEST_TIMEOUT_MS = 15000;

const COUNTRY_OPTIONS = [
  {
    value: "Belgium",
    flag: "🇧🇪",
    translationKey: "countryBelgium",
  },
  {
    value: "Netherlands",
    flag: "🇳🇱",
    translationKey: "countryNetherlands",
  },
  {
    value: "Germany",
    flag: "🇩🇪",
    translationKey: "countryGermany",
  },
  {
    value: "France",
    flag: "🇫🇷",
    translationKey: "countryFrance",
  },
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const URL_REGEX =
  /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/i;

const isValidEmailFormat = (value?: string | null) => {
  const email = value?.trim();

  if (!email) {
    return true;
  }

  return EMAIL_REGEX.test(email);
};

const isValidUrlFormat = (value?: string | null) => {
  const url = value?.trim();

  if (!url) {
    return true;
  }

  return URL_REGEX.test(url);
};

const normalizeCountryValue = (country?: string | null) => {
  if (!country) {
    return "";
  }

  const normalizedCountry = country.toString().trim().toLowerCase();

  switch (normalizedCountry) {
    case "belgium":
    case "belgie":
    case "belgië":
    case "belgique":
    case "belgien":
    case "be":
      return "Belgium";

    case "netherlands":
    case "nederland":
    case "pays-bas":
    case "paysbas":
    case "niederlande":
    case "nl":
      return "Netherlands";

    case "germany":
    case "duitsland":
    case "allemagne":
    case "deutschland":
    case "de":
      return "Germany";

    case "france":
    case "frankrijk":
    case "frankreich":
    case "fr":
      return "France";

    default:
      return "";
  }
};

const toNullableFloat = (value: any) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const parsedValue = typeof value === "number" ? value : parseFloat(value);

  return Number.isNaN(parsedValue) ? null : parsedValue;
};

const getSafeLatitude = (value: any) => {
  const parsedValue = toNullableFloat(value);

  return parsedValue === null ? DEFAULT_LATITUDE : parsedValue;
};

const getSafeLongitude = (value: any) => {
  const parsedValue = toNullableFloat(value);

  return parsedValue === null ? DEFAULT_LONGITUDE : parsedValue;
};

const joinUrl = (...parts: string[]) => {
  return parts
    .filter(Boolean)
    .map((part, index) => {
      if (index === 0) {
        return part.replace(/\/+$/g, "");
      }

      return part.replace(/^\/+/g, "").replace(/\/+$/g, "");
    })
    .join("/");
};

const getApiBaseUrl = () => {
  const rawApiUrl = (process.env.REACT_APP_API_URL || "").trim();

  if (!rawApiUrl) {
    return "/api";
  }

  const normalizedApiUrl = rawApiUrl.replace(/\/+$/g, "");

  if (normalizedApiUrl.endsWith("/api")) {
    return normalizedApiUrl;
  }

  return joinUrl(normalizedApiUrl, "api");
};

const getTerminalCrudCollectionUrl = () => {
  return joinUrl(getApiBaseUrl(), "terminal-crud/terminals");
};

const getTerminalCrudItemUrl = (id: string | number) => {
  return joinUrl(getTerminalCrudCollectionUrl(), String(id));
};

const getTerminalId = (terminalValue: any) => {
  if (!terminalValue) {
    return null;
  }

  if (terminalValue.id) {
    return terminalValue.id;
  }

  if (terminalValue["@id"]) {
    return terminalValue["@id"].toString().split("/").filter(Boolean).pop();
  }

  return null;
};

const requestJson = async (url: string, options: RequestInit = {}) => {
  const controller = new AbortController();

  const timeout = window.setTimeout(() => {
    controller.abort();
  }, REQUEST_TIMEOUT_MS);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        ...(options.headers ?? {}),
      },
    });

    const responseText = await response.text();

    if (!response.ok) {
      throw new Error(responseText || response.statusText || "Request failed");
    }

    return responseText ? JSON.parse(responseText) : null;
  } catch (error: any) {
    if (error?.name === "AbortError") {
      throw new Error(`Request timed out: ${url}`);
    }

    throw error;
  } finally {
    window.clearTimeout(timeout);
  }
};

const submitTerminalToController = async (
  payload: any,
  terminalValue?: any
) => {
  const terminalId = getTerminalId(terminalValue);
  const isUpdate = Boolean(terminalId);

  const cleanedPayload = {
    ...payload,
  };

  delete cleanedPayload.labels;

  if (!isUpdate) {
    delete cleanedPayload.id;
    delete cleanedPayload["@id"];
    delete cleanedPayload["@type"];
  }

  return requestJson(
    isUpdate
      ? getTerminalCrudItemUrl(terminalId as string | number)
      : getTerminalCrudCollectionUrl(),
    {
      method: isUpdate ? "PUT" : "POST",
      body: JSON.stringify(cleanedPayload),
    }
  );
};

const normalizeContactForForm = (contact: any = {}) => {
  return {
    ...(contact?.["@id"] ? { "@id": contact["@id"] } : {}),
    ...(contact?.id ? { id: contact.id } : {}),
    name: contact?.name ?? "",
    phoneNumber: contact?.phoneNumber ?? "",
    email: contact?.email ?? "",
  };
};

const normalizeLinkForForm = (link: any = {}) => {
  return {
    name: link?.name ?? "",
    url: link?.url ?? "",
  };
};

const normalizeLinksForSubmit = (links: any) => {
  if (!Array.isArray(links)) {
    return null;
  }

  const normalizedLinks = links
    .map((link: any) => {
      const name = link?.name?.trim() || null;
      const url = link?.url?.trim() || null;

      return {
        name,
        url,
      };
    })
    .filter((link: any) => link.name || link.url);

  return normalizedLinks.length > 0 ? normalizedLinks : null;
};

const normalizeFilesForSubmit = (fileGroup: any) => {
  if (!fileGroup || !Array.isArray(fileGroup.files)) {
    return {
      files: [],
    };
  }

  const normalizedFiles = fileGroup.files
    .filter(Boolean)
    .filter((file: any) => !(file instanceof File))
    .map((file: any) => {
      if (typeof file === "string") {
        return file;
      }

      if (file?.["@id"]) {
        return file["@id"];
      }

      if (file?.id) {
        return `/api/files/${file.id}`;
      }

      return null;
    })
    .filter(Boolean);

  return {
    files: normalizedFiles,
  };
};

const getDefaultContact = () => ({
  name: "",
  phoneNumber: "",
  email: "",
});

const getDefaultLink = () => ({
  name: "",
  url: "",
});

const MapResizeHandler = () => {
  const map = useMap();

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      map.invalidateSize();
    }, 250);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [map]);

  return null;
};

const TerminalFormMapEvents = ({
  setLatitude,
  setLongitude,
  setValue,
}: {
  setLatitude: React.Dispatch<React.SetStateAction<number>>;
  setLongitude: React.Dispatch<React.SetStateAction<number>>;
  setValue: any;
}) => {
  useMapEvents({
    click: (e: any) => {
      const latLng = e.latlng;

      const nextLatitude = parseFloat(latLng.lat.toFixed(5));
      const nextLongitude = parseFloat(latLng.lng.toFixed(5));

      setLatitude(nextLatitude);
      setLongitude(nextLongitude);

      setValue("latitude", nextLatitude, {
        shouldValidate: false,
        shouldDirty: true,
      });

      setValue("longitude", nextLongitude, {
        shouldValidate: false,
        shouldDirty: true,
      });
    },
  });

  return null;
};

const TerminalForm = ({
  terminal,
  mode,
  onClose,
  callback,
  terminalDefaults,
  initialTerminal,
}: any) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const isCreateMode = mode === "create" || !terminal;

  const createDefaults = useMemo(() => {
    return terminalDefaults || initialTerminal || {};
  }, [terminalDefaults, initialTerminal]);

  const [latitude, setLatitude] = useState(
    terminal && terminal.latitude !== null && terminal.latitude !== undefined
      ? getSafeLatitude(terminal.latitude)
      : createDefaults?.latitude !== null &&
        createDefaults?.latitude !== undefined
      ? getSafeLatitude(createDefaults.latitude)
      : DEFAULT_LATITUDE
  );

  const [longitude, setLongitude] = useState(
    terminal && terminal.longitude !== null && terminal.longitude !== undefined
      ? getSafeLongitude(terminal.longitude)
      : createDefaults?.longitude !== null &&
        createDefaults?.longitude !== undefined
      ? getSafeLongitude(createDefaults.longitude)
      : DEFAULT_LONGITUDE
  );

  const [showMap, setShowMap] = useState(false);
  const [mapLoading, setMapLoading] = useState(true);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const defaultTerminal = useMemo(() => {
    if (!isCreateMode && terminal) {
      return {
        ...terminal,
        latitude: getSafeLatitude(terminal.latitude),
        longitude: getSafeLongitude(terminal.longitude),
        country: normalizeCountryValue(terminal.country),
        color: terminal.color || DEFAULT_COLOR,
        contacts: Array.isArray(terminal.contacts)
          ? terminal.contacts.map(normalizeContactForForm)
          : [],
        links: Array.isArray((terminal as any).links)
          ? (terminal as any).links.map(normalizeLinkForForm)
          : [],
        aliases: Array.isArray(terminal.aliases) ? terminal.aliases : [],
        fileGroup: {
          files: Array.isArray((terminal as any)?.fileGroup?.files)
            ? (terminal as any).fileGroup.files
            : [],
        },
      };
    }

    return {
      name: createDefaults?.name ?? "",
      abbreviation: createDefaults?.abbreviation ?? createDefaults?.name ?? "",
      city: createDefaults?.city ?? "",
      country: normalizeCountryValue(createDefaults?.country) || "",
      color: createDefaults?.color || DEFAULT_COLOR,
      description: createDefaults?.description ?? "",
      latitude:
        createDefaults?.latitude !== null &&
        createDefaults?.latitude !== undefined
          ? getSafeLatitude(createDefaults.latitude)
          : DEFAULT_LATITUDE,
      longitude:
        createDefaults?.longitude !== null &&
        createDefaults?.longitude !== undefined
          ? getSafeLongitude(createDefaults.longitude)
          : DEFAULT_LONGITUDE,
      contacts: Array.isArray(createDefaults?.contacts)
        ? createDefaults.contacts.map(normalizeContactForForm)
        : [],
      links: Array.isArray(createDefaults?.links)
        ? createDefaults.links.map(normalizeLinkForForm)
        : [],
      aliases: [],
      fileGroup: {
        files: Array.isArray(createDefaults?.fileGroup?.files)
          ? createDefaults.fileGroup.files
          : [],
      },
    };
  }, [terminal, isCreateMode, createDefaults]);

  const {
    handleSubmit,
    control,
    formState,
    setValue,
    register,
    reset,
  } = useForm<any>({
    defaultValues: defaultTerminal,
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  const {
    fields: contactFields,
    append: appendContact,
    remove: removeContact,
  } = useFieldArray({
    control,
    name: "contacts",
    keyName: "fieldArrayId",
  });

  const {
    fields: linkFields,
    append: appendLink,
    remove: removeLink,
  } = useFieldArray({
    control,
    name: "links",
    keyName: "fieldArrayId",
  });

  const { isSubmitting, errors } = formState;
  const hasValidationErrors = Object.keys(errors).length > 0;
  const submitDisabled = isSubmitting || saving;

  useEffect(() => {
    reset(defaultTerminal);

    setLatitude(getSafeLatitude(defaultTerminal.latitude));
    setLongitude(getSafeLongitude(defaultTerminal.longitude));

    setShowMap(false);
    setMapLoading(true);

    const timeoutId = window.setTimeout(() => {
      setShowMap(true);
      setMapLoading(false);
    }, MAP_RENDER_DELAY_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [defaultTerminal, reset]);

  const handleCloseForm = useCallback(() => {

    if (typeof onClose === "function") {
      onClose();
      return;
    }

    dispatch(hideModal() as any);
  }, [dispatch, onClose]);

  const onSubmit: SubmitHandler<Terminal> = async (data: any) => {

    setSubmitError(null);
    setSaving(true);

    const normalizedContacts = Array.isArray(data.contacts)
      ? data.contacts
          .map((contact: any) => ({
            ...(contact?.["@id"] ? { "@id": contact["@id"] } : {}),
            ...(contact?.id ? { id: contact.id } : {}),
            name: contact?.name?.trim() || null,
            phoneNumber: contact?.phoneNumber?.trim() || null,
            email: contact?.email?.trim() || null,
          }))
          .filter(
            (contact: any) =>
              contact["@id"] ||
              contact.id ||
              contact.name ||
              contact.phoneNumber ||
              contact.email
          )
      : [];

    const formData = {
      ...data,
      latitude: toNullableFloat(data.latitude),
      longitude: toNullableFloat(data.longitude),
      country: normalizeCountryValue(data.country) || null,
      contacts: normalizedContacts,
      links: normalizeLinksForSubmit(data.links),
      fileGroup: normalizeFilesForSubmit(data.fileGroup),
    };

    delete (formData as any).labels;

    try {
      const resultTerminal = await submitTerminalToController(
        formData,
        isCreateMode ? null : terminal
      );

      const savedTerminal = {
        ...(terminal ?? {}),
        ...formData,
        ...(resultTerminal && typeof resultTerminal === "object"
          ? resultTerminal
          : {}),
      };

      delete (savedTerminal as any).labels;

      if (typeof callback === "function" && callback.length <= 1) {
        await callback(savedTerminal);
      }

      handleCloseForm();

      window.setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent("terminal-crud:saved", {
            detail: savedTerminal,
          })
        );
      }, 0);
    } catch (error: any) {
      console.error("Terminal save failed", error);
      setSubmitError(error?.message || "Failed to save terminal");
      setSaving(false);
    }
  };

  const safeMapLatitude = getSafeLatitude(latitude);
  const safeMapLongitude = getSafeLongitude(longitude);
  const mapKey = `${safeMapLatitude}-${safeMapLongitude}`;

  const renderMapContent = () => {
    if (mapLoading || !showMap) {
      return (
        <Box
          sx={{
            height: "100%",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            bgcolor: "background.default",
          }}
        >
          <Box sx={{ px: 4 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              sx={{ mb: 2 }}
            >
              {t("loadingMap") || "Loading map..."}
            </Typography>

            <LinearProgress />
          </Box>
        </Box>
      );
    }

    return (
      <MapContainer
        key={mapKey}
        center={[safeMapLatitude, safeMapLongitude]}
        zoom={12}
        scrollWheelZoom={false}
        style={{
          height: "100%",
          width: "100%",
        }}
      >
        <MapResizeHandler />

        <TerminalFormMapEvents
          setLatitude={setLatitude}
          setLongitude={setLongitude}
          setValue={setValue}
        />

        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <Marker position={[safeMapLatitude, safeMapLongitude]}>
          <Popup>{`[${safeMapLatitude}, ${safeMapLongitude}]`}</Popup>
        </Marker>
      </MapContainer>
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={2} alignItems="stretch">
        {saving ? (
          <Grid item xs={12}>
            <LinearProgress />
          </Grid>
        ) : null}

        {submitError ? (
          <Grid item xs={12}>
            <Alert severity="error">{submitError}</Alert>
          </Grid>
        ) : null}

        {hasValidationErrors ? (
          <Grid item xs={12}>
            <Alert severity="warning">
              {t("pleaseCheckRequiredFields") || "Please check required fields."}
            </Alert>
          </Grid>
        ) : null}

        <Grid item xs={12} md={4}>
          <Stack spacing={1.5}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
              }}
            >
              <Stack spacing={1.5}>
                <Grid container className="form-fields-container">
                  <Grid item xs={12}>
                    <InputTextField
                      name="name"
                      label={t("name")}
                      control={control}
                      required
                    />
                  </Grid>
                </Grid>

                <Grid
                  container
                  className="form-fields-container"
                  gap={0.5}
                  flexWrap="nowrap"
                >
                  <Grid item xs={12} sm={6}>
                    <InputTextField
                      name="abbreviation"
                      label={t("abbreviation")}
                      control={control}
                      required
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <InputTextField
                      name="city"
                      label={t("city")}
                      control={control}
                      required
                    />
                  </Grid>
                </Grid>

                <Grid
                  container
                  className="form-fields-container"
                  gap={0.5}
                  flexWrap="nowrap"
                >
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="country"
                      control={control}
                      render={({ field }) => {
                        const selectedCountry = normalizeCountryValue(
                          field.value
                        );

                        return (
                          <FormControl fullWidth size="small">
                            <InputLabel id="terminal-country-label">
                              {t("country")} *
                            </InputLabel>

                            <Select
                              labelId="terminal-country-label"
                              id="terminal-country"
                              value={selectedCountry}
                              label={t("country")}
                              onChange={(event) => {
                                field.onChange(event.target.value);
                              }}
                              onBlur={field.onBlur}
                              inputRef={field.ref}
                              MenuProps={{
                                sx: {
                                  zIndex: 99999,
                                },
                                PaperProps: {
                                  sx: {
                                    zIndex: 100000,
                                    maxHeight: 320,
                                  },
                                },
                              }}
                              required
                            >
                              <MenuItem value="">{t("chooseOne")}</MenuItem>

                              {COUNTRY_OPTIONS.map((country) => (
                                <MenuItem
                                  key={country.value}
                                  value={country.value}
                                >
                                  <Box
                                    component="span"
                                    sx={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <span>{country.flag}</span>
                                    <span>{t(country.translationKey)}</span>
                                  </Box>
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        );
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <InputColorField
                      name="color"
                      label={t("color")}
                      control={control}
                      required
                    />
                  </Grid>
                </Grid>

                <Grid
                  container
                  className="form-fields-container"
                  gap={0.5}
                  flexWrap="nowrap"
                >
                  <Grid item xs={12} sm={6}>
                    <NumberField
                      name="latitude"
                      label="Latitude"
                      control={control}
                      required={false}
                      decimal={5}
                      value={latitude}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <NumberField
                      name="longitude"
                      label="Longitude"
                      control={control}
                      required={false}
                      decimal={5}
                      value={longitude}
                    />
                  </Grid>
                </Grid>

                <Grid container className="form-fields-container">
                  <Grid item xs={12}>
                    <InputTextareaField
                      name="description"
                      label={t("description")}
                      control={control}
                    />
                  </Grid>
                </Grid>

                <Divider />

                <Grid container className="form-fields-container">
                  <Grid item xs={12}>
                    <AliasFields control={control} register={register} t={t} />
                  </Grid>
                </Grid>
              </Stack>
            </Paper>
          </Stack>
        </Grid>

        <Grid item xs={12} md={8}>
          <Stack spacing={1.5} sx={{ height: "100%" }}>
            <Paper
              variant="outlined"
              sx={{
                height: {
                  xs: 360,
                  md: 420,
                },
                minHeight: {
                  xs: 360,
                  md: 420,
                },
                borderRadius: 2,
                overflow: "hidden",
                "& .leaflet-container": {
                  height: "100%",
                  width: "100%",
                },
              }}
            >
              {renderMapContent()}
            </Paper>

            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
              <Grid
                container
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1 }}
              >
                <Grid item>
                  <Typography variant="h6" className="form-fields-title">
                    {t("contacts")}
                  </Typography>
                </Grid>

                <Grid item>
                  <Button
                    type="button"
                    size="small"
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => appendContact(getDefaultContact())}
                    disabled={saving}
                  >
                    {t("addFormTitleByType", { type: t("contact") })}
                  </Button>
                </Grid>
              </Grid>

              <Box
                sx={{
                  maxHeight: { xs: 260, md: 300 },
                  overflow: "auto",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1.5,
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: "30%" }}>{t("name")}</TableCell>
                      <TableCell sx={{ width: "25%" }}>{t("phone")}</TableCell>
                      <TableCell sx={{ width: "35%" }}>{t("email")}</TableCell>
                      <TableCell align="right" sx={{ width: 90 }}>
                        {t("actions")}
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {contactFields.map((field: any, index) => (
                      <TableRow key={field.fieldArrayId}>
                        <TableCell>
                          <input
                            type="hidden"
                            {...register(`contacts.${index}.@id`)}
                          />
                          <input
                            type="hidden"
                            {...register(`contacts.${index}.id`)}
                          />

                          <TextField
                            size="small"
                            fullWidth
                            placeholder={t("name")}
                            disabled={saving}
                            {...register(`contacts.${index}.name`)}
                          />
                        </TableCell>

                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            placeholder={t("phone")}
                            disabled={saving}
                            {...register(`contacts.${index}.phoneNumber`)}
                          />
                        </TableCell>

                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            type="email"
                            placeholder={t("email")}
                            disabled={saving}
                            error={Boolean(
                              (errors as any)?.contacts?.[index]?.email
                            )}
                            helperText={
                              (errors as any)?.contacts?.[index]?.email?.message
                            }
                            {...register(`contacts.${index}.email`, {
                              validate: (value) =>
                                isValidEmailFormat(value) ||
                                t("invalidEmailFormat"),
                            })}
                          />
                        </TableCell>

                        <TableCell align="right">
                          <IconButton
                            type="button"
                            size="small"
                            color="error"
                            disabled={saving}
                            onClick={() => removeContact(index)}
                          >
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}

                    {contactFields.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            textAlign="center"
                            sx={{ py: 2 }}
                          >
                            {t("noContactsForTerminal")}
                          </Typography>

                          <Box display="flex" justifyContent="center" pb={1}>
                            <Button
                              type="button"
                              size="small"
                              startIcon={<AddIcon />}
                              disabled={saving}
                              onClick={() => appendContact(getDefaultContact())}
                            >
                              {t("addFormTitleByType", { type: t("contact") })}
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </Box>
            </Paper>

            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
              <Grid
                container
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1 }}
              >
                <Grid item>
                  <Typography variant="h6" className="form-fields-title">
                    {t("links")}
                  </Typography>
                </Grid>

                <Grid item>
                  <Button
                    type="button"
                    size="small"
                    variant="contained"
                    startIcon={<AddIcon />}
                    disabled={saving}
                    onClick={() => appendLink(getDefaultLink())}
                  >
                    {t("addFormTitleByType", { type: t("link") })}
                  </Button>
                </Grid>
              </Grid>

              <Box
                sx={{
                  maxHeight: { xs: 220, md: 260 },
                  overflow: "auto",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1.5,
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: "38%" }}>{t("name")}</TableCell>
                      <TableCell sx={{ width: "52%" }}>{t("url")}</TableCell>
                      <TableCell align="right" sx={{ width: 90 }}>
                        {t("actions")}
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {linkFields.map((field: any, index) => (
                      <TableRow key={field.fieldArrayId}>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            placeholder={t("name")}
                            disabled={saving}
                            {...register(`links.${index}.name`)}
                          />
                        </TableCell>

                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            placeholder={t("url")}
                            disabled={saving}
                            error={Boolean(
                              (errors as any)?.links?.[index]?.url
                            )}
                            helperText={
                              (errors as any)?.links?.[index]?.url?.message
                            }
                            {...register(`links.${index}.url`, {
                              validate: (value) =>
                                isValidUrlFormat(value) ||
                                t("invalidUrlFormat"),
                            })}
                          />
                        </TableCell>

                        <TableCell align="right">
                          <IconButton
                            type="button"
                            size="small"
                            color="error"
                            disabled={saving}
                            onClick={() => removeLink(index)}
                          >
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}

                    {linkFields.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            textAlign="center"
                            sx={{ py: 2 }}
                          >
                            {t("noLinksForTerminal")}
                          </Typography>

                          <Box display="flex" justifyContent="center" pb={1}>
                            <Button
                              type="button"
                              size="small"
                              startIcon={<AddIcon />}
                              disabled={saving}
                              onClick={() => appendLink(getDefaultLink())}
                            >
                              {t("addFormTitleByType", { type: t("link") })}
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </Box>
            </Paper>
          </Stack>
        </Grid>
      </Grid>

      <Grid item xs={12} className="button-wrapper">
        <Button
          type="button"
          onClick={handleCloseForm}
          disabled={saving}
          variant="contained"
        >
          <CloseIcon fontSize="inherit" />
          &nbsp;{t("close")}
        </Button>

        <Button type="submit" disabled={submitDisabled} variant="contained">
          <SaveIcon fontSize="inherit" />
          &nbsp;{saving ? t("saving") || "Saving..." : t("save")}
        </Button>
      </Grid>
    </form>
  );
};

export default TerminalForm;